import { CdkDrag, CdkDragHandle } from '@angular/cdk/drag-drop';
import { NgClass } from '@angular/common';
import { Component, Inject, Input, OnInit, inject } from '@angular/core';
import { FormsModule } from "@angular/forms";
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from "@angular/material/card";
import { MatOptionModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MAT_DIALOG_DATA, MatDialog, MatDialogActions, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { MatSelectModule } from '@angular/material/select';
import { DatePickerModule, PopupSettings, TimePickerModule } from '@progress/kendo-angular-dateinputs';
import { DropDownListModule } from '@progress/kendo-angular-dropdowns';
import { FormFieldModule, TextAreaModule, TextBoxModule } from '@progress/kendo-angular-inputs';
import { FloatingLabelModule } from '@progress/kendo-angular-label';
import { QueryClient } from '@tanstack/angular-query-experimental';
import { ActivityInfo, ActivityStatus, MechanicType } from '../../../components/calendars/calendars.models';
import { CalendarsService } from '../../../components/calendars/calendars.service';
import { ProgressActivitiesInfo } from '../../../components/tools/tools.models';
import { AuthorisationService } from '../../../shared/services/authorisation.service';
import { CommonDataService } from '../../../shared/services/common-data.service';
import { CommonService } from '../../../shared/services/common.service';
import { ConfigurationService } from '../../../shared/services/configuration.service';
import { ContactTypeUser } from '../../models/common.models';
import { SaveResult } from '../../models/result.model';
import { ConfirmationDialog } from '../confirmation-dialog/confirmation-dialog.component';
import { ContactsSelectorComponent } from '../contacts-selector/contacts-selector.component';
import { ActivitiesUpdaterService } from './activities-updater.service';

@Component({
  selector: 'activities-updater',
  templateUrl: './activities-updater.component.html',
  styleUrls: ['./activities-updater.component.scss'],
  providers: [ActivitiesUpdaterService],
  standalone: true,
  imports: [
    CdkDrag, CdkDragHandle, MatDialogTitle, MatProgressSpinnerModule, DropDownListModule, MatCardModule,
    FormsModule, NgClass, DatePickerModule, TimePickerModule, MatFormFieldModule, MatSelectModule,
    MatOptionModule, MatInputModule, MatDatepickerModule, FormFieldModule, FloatingLabelModule,
    TextAreaModule, TextBoxModule, MatDialogActions, MatButtonModule, ContactsSelectorComponent
  ]
})
export class ActivitiesUpdaterComponent implements OnInit
{
  public saveInProgress = false;

  // Existing fields
  public CreditLineDueDate: Date;
  public CreditLine: string = "";
  public CreditLineNotes = "";
  public OnHoldDate: Date;
  public disableHeldDate = false;
  public CreditLineRotationTypeId = 0;
  public CreditLineTypeId = 0;

  public showCreditLineType = false;
  public showCreditLineRotation = false;
  public showCreditLineNotes = false;
  public showCreditLine = true;

  private _statuses: ActivityStatus[] = [];
  private _heldUntilText = "";
  private _activityStatusClass: any = { 'unconfirmed-sold-activity-text': true };
  private _currentActivityStatus: ActivityStatus = { Name: 'Submitted For Delivery', Value: 'IsUnConfirmedSold' };

  // NEW: Activity-level fields
  public Product: string = "";
  public BookingId: string = "";
  public Scripts: number = null;
  public ClientExecUserId: number = null;
  public ClientSupportUserId: number = null;
  public BriefManagerUserId: number = null;
  public ProgrammerUserId: number = null;
  public MechanicTypeId: number = null;
  public MajorPrize: string = "";
  public MajorPrizeHowFulFilled: string = "";
  public MinorPrize: string = "";
  public MinorPrizeHowFulFilled: string = "";

  // NEW: Station-specific info fields (applied uniformly)
  public ActivityName: string = "";
  public Mechanic: string = "";
  public AirtimeBreakdown: string = "";
  public Revenue: number = null;
  public Production: number = null;
  public MultimediaRecovery: number = null;
  public OnAirTime: Date = null;
  public StartTime: Date = null;
  public EndTime: Date = null;
  public OnsiteContactName: string = "";
  public OnsiteContactTel: string = "";
  public TicketingOutlet: string = "";
  public LocationAddress: string = "";
  public SignageRequired: string = "";

  // Contact selectors (bind to objects, extract Ids on save)
  public currentClientExecutive: ContactTypeUser = null;
  public currentClientSupport: ContactTypeUser = null;
  public currentBriefManager: ContactTypeUser = null;
  public currentProgrammer: ContactTypeUser = null;

  // MechanicType selector (bind to object, extract Id on save)
  public currentMechanicType: MechanicType = null;

  private queryClient = inject(QueryClient);

  @Input() get activities(): ActivityInfo[]
  {
    return this.activitiesUpdaterService.activities;
  }
  set activities(value: ActivityInfo[])
  {
    this.activitiesUpdaterService.activities = value;
  }

  public commonService = inject(CommonService);
  public configurationService = inject(ConfigurationService);
  public commonDataService = inject(CommonDataService);
  private calendarsService = inject(CalendarsService);
  private authorisationService = inject(AuthorisationService);
  private activitiesUpdaterService = inject(ActivitiesUpdaterService);

  public popupSettings: PopupSettings = {
    appendTo: 'root',
    animate: false
  };

  constructor(
    private dialogRef: MatDialogRef<ActivitiesUpdaterComponent>,
    private confirmDialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) private data: ProgressActivitiesInfo)
  {
  }

  ngOnInit(): void
  {
    this.activities = this.data.SelectedActivities;
    this.setUpActivityStatus(this.currentActivityStatus.Value);
  }

  ngOnDestroy() { }

  get statuses(): ActivityStatus[]
  {
    //Don't allow IsGuaranteed
    this._statuses = this.calendarsService.statuses.filter(s => s.Value != "IsGuaranteed");

    if (!this.authorisationService.hasPermission(this.authorisationService.Permissions.ChangeActivityToHoldStatus))
    {
      this._statuses = this._statuses.filter(s => s.Value != "OnHold");
    }
    if (!this.authorisationService.hasPermission(this.authorisationService.Permissions.ChangeActivityToSFDStatus))
    {
      this._statuses = this._statuses.filter(s => s.Value != "IsUnConfirmedSold");
    }

    if (!this.configurationService.cbSettings().allowProgressActivitiesToConfirm
      || !this.authorisationService.hasPermission(this.authorisationService.Permissions.ChangeActivityToConfirmedStatus))
    {
      this._statuses = this._statuses.filter(s => s.Value != "IsSold");
    }

    return this._statuses;
  }

  get mechanicTypes(): MechanicType[]
  {
    // simple: include all, show Disabled as well (no filter pipe)
    return this.calendarsService.mechanicTypes?.() ?? [];
  }

  handleActivityStatusChange(event: ActivityStatus)
  {
    this.currentActivityStatus = event;
    this.setUpActivityStatus(this.currentActivityStatus.Value);
  }

  heldDateChange(event: Date)
  {
    if (this.currentActivityStatus.Value == "Pending" || this.currentActivityStatus.Value == "IsSold")
    {
      this.OnHoldDate = null;
    }
    else
    {
      this.OnHoldDate = this.commonService.ensureDateIsNotOnWeekend(event);
    }
  }

  setUpActivityStatus(status: string)
  {
    this._activityStatusClass = { 'pending-activity-text': true };
    this._heldUntilText = "";

    switch (status)
    {
      case "Pending":
        this._activityStatusClass = { 'pending-activity-text': true };
        this._heldUntilText = "";
        break;
      case "IsSold":
        this._activityStatusClass = { 'sold-activity-text': true };
        this._heldUntilText = "";
        break;
      case "IsGuaranteed":
        this._activityStatusClass = { 'guaranteed-activity-text': true };
        this._heldUntilText = "Held Until Date";
        break;
      case "OnHold":
        this._activityStatusClass = { 'onhold-activity-text': true };
        this._heldUntilText = "Held Until Date";
        break;
      case "IsUnConfirmedSold":
        this._activityStatusClass = { 'unconfirmed-sold-activity-text': true };
        this._heldUntilText = "Held Until Date";
        break;
      default:
        this._activityStatusClass = { 'pending-activity-text': true };
        this._heldUntilText = "";
        break;
    }

    this.disableHeldDate = true;
    this.OnHoldDate = null;

    if (this.currentActivityStatus.Value == "OnHold" || this.currentActivityStatus.Value == "IsUnConfirmedSold")
    {
      let onholddate: Date = this.commonService.today;

      switch (this.currentActivityStatus.Value)
      {
        case "OnHold":
          if (this.authorisationService.hasPermission(this.authorisationService.Permissions.ChangeOnHoldHeldDate))
          {
            this.disableHeldDate = false;
          }
          break;
        case "IsUnConfirmedSold":
          if (this.authorisationService.hasPermission(this.authorisationService.Permissions.ChangeSFDHeldDate))
          {
            this.disableHeldDate = false;
          }
          break;
      }

      onholddate = this.commonService.ensureDateIsNotOnWeekend(onholddate);
      this.OnHoldDate = onholddate;
    }
  }

  get currentActivityStatus(): ActivityStatus { return this._currentActivityStatus; }
  set currentActivityStatus(value: ActivityStatus) { this._currentActivityStatus = value; }

  get heldUntilText(): any { return this._heldUntilText; }
  get activityStatusClass(): any { return this._activityStatusClass; }

  progressActivities(event: any)
  {
    try
    {
      this.saveInProgress = true;

      if (this.activities && this.activities.length > 0)
      {
        const progressActivitiesInfo: ProgressActivitiesInfo = this.data;
        progressActivitiesInfo.ActivityIds = this.activities.map(a => a.Id);
        progressActivitiesInfo.Status = this.currentActivityStatus.Value;

        // existing
        progressActivitiesInfo.CreditLine = this.CreditLine;
        progressActivitiesInfo.CreditLineDueDate = this.CreditLineDueDate;
        progressActivitiesInfo.HeldDate = this.OnHoldDate;

        // NEW: map contacts/mechanic type Ids
        progressActivitiesInfo.Product = this.Product;
        progressActivitiesInfo.ClientExecUserId = this.currentClientExecutive?.Id ?? null;
        progressActivitiesInfo.ClientSupportUserId = this.currentClientSupport?.Id ?? null;
        progressActivitiesInfo.BriefManagerUserId = this.currentBriefManager?.Id ?? null;
        progressActivitiesInfo.ProgrammerUserId = this.currentProgrammer?.Id ?? null;
        progressActivitiesInfo.MechanicTypeId = this.currentMechanicType?.Id ?? null;

        // NEW: activity-level
        progressActivitiesInfo.BookingId = this.BookingId || null;
        progressActivitiesInfo.Scripts = this.Scripts ?? null;
        progressActivitiesInfo.MajorPrize = this.MajorPrize || null;
        progressActivitiesInfo.MajorPrizeHowFulFilled = this.MajorPrizeHowFulFilled || null;
        progressActivitiesInfo.MinorPrize = this.MinorPrize || null;
        progressActivitiesInfo.MinorPrizeHowFulFilled = this.MinorPrizeHowFulFilled || null;

        // NEW: station-specific (uniform)
        progressActivitiesInfo.ActivityName = this.ActivityName || null;
        progressActivitiesInfo.Mechanic = this.Mechanic || null;
        progressActivitiesInfo.AirtimeBreakdown = this.AirtimeBreakdown || null;
        progressActivitiesInfo.Revenue = this.Revenue ?? null;
        progressActivitiesInfo.Production = this.Production ?? null;
        progressActivitiesInfo.MultimediaRecovery = this.MultimediaRecovery ?? null;
        progressActivitiesInfo.OnAirTime = this.OnAirTime ?? null;
        progressActivitiesInfo.StartTime = this.StartTime ?? null;
        progressActivitiesInfo.EndTime = this.EndTime ?? null;
        progressActivitiesInfo.OnsiteContactName = this.OnsiteContactName || null;
        progressActivitiesInfo.OnsiteContactTel = this.OnsiteContactTel || null;
        progressActivitiesInfo.TicketingOutlet = this.TicketingOutlet || null;
        progressActivitiesInfo.LocationAddress = this.LocationAddress || null;
        progressActivitiesInfo.SignageRequired = this.SignageRequired || null;

        if (progressActivitiesInfo.ActivityIds && progressActivitiesInfo.ActivityIds.length > 0)
        {
          this.activitiesUpdaterService.progressActivities(progressActivitiesInfo).then((sr: SaveResult) =>
          {
            this.saveInProgress = false;

            progressActivitiesInfo.SaveResult = sr;

            if (sr.IsSuccessful)
            {
              if (sr.Message) { this.commonService.notifySuccess("Success", sr.Message); }

              this.queryClient.invalidateQueries({
                type: "all", exact: false, predicate: (query: any) =>
                {
                  return query.queryKey[0] == "Calendar" || query.queryKey[0] == "Activities";
                }
              });

              this.activities.map(a =>
              {
                this.queryClient.invalidateQueries({
                  type: "all", exact: false, predicate: (query: any) =>
                  {
                    return query.queryKey[0] == "Activity" && (query.queryKey[1] == null || query.queryKey[1] == a.Id || query.queryKey[1] == 0);
                  }
                });
              });
            }
            else
            {
              if (sr.Message)
              {
                const confirmDialogRef: MatDialogRef<ConfirmationDialog> = this.confirmDialog.open(ConfirmationDialog, { disableClose: true });
                confirmDialogRef.componentInstance.confirmMessage = sr.Message;
                confirmDialogRef.componentInstance.confirmHeader = sr.MessageHeader;
                confirmDialogRef.componentInstance.confirmTitle = sr.MessageTitle;
                confirmDialogRef.componentInstance.showCancelButton = false;
                confirmDialogRef.componentInstance.confirmText = "OK";

                if (sr.CssClassName)
                {
                  confirmDialogRef.addPanelClass(sr.CssClassName);
                }
              }
              else
              {
                let errMsg = "Unable to save record.";
                if (sr.ErrorMessage) { errMsg = sr.ErrorMessage; }
                this.commonService.notifyFailure("Error", errMsg, sr.ExceptionMessage, sr.ValidationExceptionMessage);
              }
            }

            this.dialogRef.close(progressActivitiesInfo);
          }).catch(err => this.saveInProgress = false);
        }
      }
    }
    catch
    {
      this.saveInProgress = false;
    }
  }

  onCancelClick()
  {
    this.dialogRef.close();
  }
}
